/**
 * The Create Vetting Request form's draft (US-00-004).
 *
 * Same reasoning as the query draft, plus one criterion of its own:
 * US-00-004.20 — a session that expires while the form is being filled sends the
 * user through OTP re-verification and returns them "to the form with their
 * entered text intact", having created nothing.
 *
 * TWO NAMED SLOTS, NOT A LIST. The notice accepts PDF/JPG/PNG and the draft
 * additionally accepts DOCX (US-00-004.3), and submission has to be blocked with
 * a message against EACH missing upload (US-00-004.2). A single array of two
 * files could express neither.
 */

import { newIdempotencyKey } from "@anthra/platform/api"
import {
	type FileRejection,
	type PlatformFile,
	validateFile,
	VETTING_DRAFT_RULES,
	VETTING_NOTICE_RULES,
} from "@anthra/platform/files"
import { create } from "zustand"

export type VettingSlot = "notice" | "draft"

type VettingDraftState = {
	title: string
	notes: string
	notice: PlatformFile | null
	draft: PlatformFile | null
	/** The rejection against each slot, cleared when an acceptable file replaces it. */
	rejections: Record<VettingSlot, FileRejection | null>
	idempotencyKey: string | null

	setTitle: (value: string) => void
	setNotes: (value: string) => void
	/** Rejects with the rule that was broken and LEAVES the existing file in place. */
	setFile: (slot: VettingSlot, file: PlatformFile) => void
	clearFile: (slot: VettingSlot) => void
	submissionKey: () => string
	reset: () => void
}

const emptyRejections: Record<VettingSlot, FileRejection | null> = { notice: null, draft: null }

export const useVettingDraftStore = create<VettingDraftState>()((set, get) => ({
	title: "",
	notes: "",
	notice: null,
	draft: null,
	rejections: { ...emptyRejections },
	idempotencyKey: null,

	setTitle: value => {
		set({ title: value })
	},

	setNotes: value => {
		set({ notes: value })
	},

	setFile: (slot, file) => {
		const rules = slot === "notice" ? VETTING_NOTICE_RULES : VETTING_DRAFT_RULES
		const rejection = validateFile(file, rules)
		if (rejection) {
			// The previously chosen file stays: a user who picks a DOCX for the
			// notice slot has not thereby unpicked the PDF they chose before.
			set(state => ({ rejections: { ...state.rejections, [slot]: rejection } }))
			return
		}
		set(state => ({
			...state,
			[slot]: file,
			rejections: { ...state.rejections, [slot]: null },
		}))
	},

	clearFile: slot => {
		set(state => ({
			...state,
			[slot]: null,
			rejections: { ...state.rejections, [slot]: null },
		}))
	},

	submissionKey: () => {
		const existing = get().idempotencyKey
		if (existing !== null) return existing
		const key = newIdempotencyKey()
		set({ idempotencyKey: key })
		return key
	},

	reset: () => {
		set({
			title: "",
			notes: "",
			notice: null,
			draft: null,
			rejections: { ...emptyRejections },
			idempotencyKey: null,
		})
	},
}))

/**
 * Which required uploads are missing (US-00-004.2).
 *
 * Returned as a list of slots rather than a boolean so the form can put an inline
 * message against each one.
 */
export function missingUploads(
	state: Pick<VettingDraftState, "notice" | "draft">,
): readonly VettingSlot[] {
	const missing: VettingSlot[] = []
	if (state.notice === null) missing.push("notice")
	if (state.draft === null) missing.push("draft")
	return missing
}
